<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { registerCreditMessages } from '../i18n'
import { useCreditStore } from '../stores/creditStore'

import CreditPanel from './CreditPanel.vue'
import CreditUpgradeModal from './CreditUpgradeModal.vue'

import { useAccount } from '@/features/account'
import { useEnv } from '@/shared/config'
import { useI18n, useLocaleStore } from '@/shared/i18n'
import { Icon, Popover, Skeleton } from '@/shared/ui'

const CREDITS_SLOT_KEY = 'credits.slot.credits'

const store = useCreditStore()
const { accountId } = useAccount()
const selfServeEnabled = useEnv().CREDIT_SELF_SERVE_ENABLED
const localeStore = useLocaleStore()
const { t, te } = useI18n()
const messagesReady = ref(te(CREDITS_SLOT_KEY))

watch(
  () => localeStore.locale,
  async (locale) => {
    messagesReady.value = te(CREDITS_SLOT_KEY)
    if (messagesReady.value) return
    try {
      await registerCreditMessages(locale)
      messagesReady.value = true
    } catch {
      // Ambient chrome fails closed; a missing locale chunk must not expose raw message keys.
    }
  },
  { immediate: true },
)

onMounted(() => {
  void store.fetchBalance()
})

const hasAccount = computed(() => accountId.value !== undefined)
const showLoading = computed(
  () =>
    hasAccount.value &&
    (!messagesReady.value ||
      (!store.hasBalance && !store.error && (!store.loaded || store.loading))),
)
const showSlot = computed(() => hasAccount.value && messagesReady.value && store.hasBalance)
const triggerLabel = computed(() => {
  if (store.isTrial) {
    const days = store.trialDaysRemaining
    return days === null
      ? t('credits.slot.trial')
      : `${t('credits.slot.trial')} · ${t('credits.slot.daysLeft', days)}`
  }
  return t(CREDITS_SLOT_KEY, store.remaining)
})
</script>

<template>
  <span
    v-if="showLoading"
    class="ai-credit-slot ai-credit-slot--loading inline-flex min-h-8 items-center gap-xs px-md border border-border rounded-full bg-surface text-fg font-sans text-[0.8125rem] font-semibold whitespace-nowrap min-w-30 p-0 border-0 overflow-hidden max-[76rem]:px-sm"
    data-testid="ai-credit-slot-loading"
  >
    <Skeleton width="7.5rem" height="2rem" radius="var(--ui-radius-full)" />
  </span>

  <Popover
    v-else-if="showSlot"
    trigger-variant="plain"
    panel-variant="plain"
    placement="bottom-start"
    :offset="12"
    :trigger-label="triggerLabel"
    :panel-label="t('credits.panel.aiCredits')"
  >
    <template #trigger="{ open }">
      <span
        class="ai-credit-slot inline-flex min-h-8 items-center gap-xs px-md border border-border rounded-full bg-surface text-fg font-sans text-[0.8125rem] font-semibold transition-colors hover:bg-surface-sunken"
        :class="[`ai-credit-slot--${store.slotState}`, { 'ai-credit-slot--open': open }]"
        :data-state="store.slotState"
        data-testid="ai-credit-slot"
      >
        <Icon
          v-if="store.slotState === 'trial'"
          class="ai-credit-slot__trial-icon flex-none"
          name="sparkles"
          :size="14"
          aria-hidden="true"
        />
        <span
          v-else
          class="ai-credit-slot__dot w-2 h-2 flex-none rounded-full bg-success"
          aria-hidden="true"
        />
        <span class="ai-credit-slot__label w-max flex flex-1">{{ triggerLabel }}</span>
        <Icon
          class="ai-credit-slot__chevron transition-transform duration-200"
          :class="{ 'rotate-180': open }"
          name="chevron-down"
          :size="11"
          aria-hidden="true"
        />
      </span>
    </template>

    <template #default="{ close }">
      <CreditPanel @close="close" />
    </template>
  </Popover>

  <!-- Kept mounted at the ambient shell boundary so every credit guard opens the same modal. -->
  <CreditUpgradeModal :self-serve-enabled="selfServeEnabled" />
</template>

<style scoped>
.ai-credit-slot--loading :deep(.ai-credit-slot--trial) {
  border-color: var(--ui-color-info-soft);
  color: var(--ui-color-info);
}
.ai-credit-slot--loading :deep(.ai-credit-slot--trial .ai-credit-slot__dot) {
  background-color: var(--ui-color-info);
}
.ai-credit-slot--loading :deep(.ai-credit-slot--low) {
  border-color: var(--ui-color-warning-soft);
  color: var(--ui-color-warning);
}
.ai-credit-slot--loading :deep(.ai-credit-slot--low .ai-credit-slot__dot) {
  background-color: var(--ui-color-warning);
}
.ai-credit-slot--loading :deep(.ai-credit-slot--depleted) {
  border-color: var(--ui-color-danger-soft);
  color: var(--ui-color-danger);
}
.ai-credit-slot--loading :deep(.ai-credit-slot--depleted .ai-credit-slot__dot) {
  background-color: var(--ui-color-danger);
}

.ai-credit-slot--trial {
  border-color: var(--ui-color-info-soft);
  color: var(--ui-color-info);
}
.ai-credit-slot--trial .ai-credit-slot__dot {
  background-color: var(--ui-color-info);
}
.ai-credit-slot--low {
  border-color: var(--ui-color-warning-soft);
  color: var(--ui-color-warning);
}
.ai-credit-slot--low .ai-credit-slot__dot {
  background-color: var(--ui-color-warning);
}
.ai-credit-slot--depleted {
  border-color: var(--ui-color-danger-soft);
  color: var(--ui-color-danger);
}
.ai-credit-slot--depleted .ai-credit-slot__dot {
  background-color: var(--ui-color-danger);
}
</style>
